import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Send } from "lucide-react";
import { SITE } from "@/constants/testIds";

const BACKEND = (process.env.REACT_APP_BACKEND_URL || "https://moduleris-api.isvecli.com").replace(/\/$/, "");
const API = `${BACKEND}/api`;

const services = [
  "GES / Güneş Enerjisi",
  "Elektrik Tesisatı",
  "Mimari & Villa Projeleri",
  "Tarihi Restorasyon",
  "Modüler Yapılar",
  "Diğer",
];

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: services[0],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState(null);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErr(null);
    setOk(false);
    try {
      await axios.post(`${API}/quote`, form);
      setOk(true);
      toast.success("Teklif isteğiniz alındı. En kısa sürede dönüş yapacağız.");
      setForm({
        name: "",
        email: "",
        phone: "",
        service: services[0],
        message: "",
      });
    } catch (e) {
      const msg =
        e?.response?.data?.detail?.[0]?.msg ||
        e?.response?.data?.detail ||
        "Bir hata oluştu. Lütfen tekrar deneyin.";
      setErr(typeof msg === "string" ? msg : "Bir hata oluştu.");
      toast.error("Form gönderilemedi.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 border-b border-neutral-900 relative">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Left: info + map */}
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-5">
            <span className="w-6 h-px bg-[#ff5a00]" />
            <span className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-[#ff5a00]">
              İletişim / 05
            </span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-white">
            Projenizi birlikte <span className="italic font-light text-neutral-400">hayata geçirelim</span>.
          </h2>
          <p className="mt-6 text-neutral-400 text-sm md:text-base max-w-md">
            Mühendislik, enerji veya mimari ihtiyaçlarınız için ekibimizden hızlıca teknik teklif ve
            danışmanlık alın.
          </p>

          <ul className="mt-10 space-y-5">
            <li className="flex gap-4 items-start">
              <MapPin size={18} className="text-[#ff5a00] mt-1 shrink-0" />
              <div>
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-neutral-500 mb-1">
                  Adres
                </div>
                <div className="text-neutral-200 text-sm">
                  Küner Mah. 2818 Sokak, Yeşil Ev, Menderes / İzmir
                </div>
              </div>
            </li>
            <li className="flex gap-4 items-start">
              <Phone size={18} className="text-[#ff5a00] mt-1 shrink-0" />
              <div>
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-neutral-500 mb-1">
                  Telefon
                </div>
                <a
                  href="tel:+905456534494"
                  className="text-neutral-200 text-sm hover:text-[#ff5a00] transition-colors"
                >
                  +90 545 653 44 94
                </a>
              </div>
            </li>
            <li className="flex gap-4 items-start">
              <Mail size={18} className="text-[#ff5a00] mt-1 shrink-0" />
              <div>
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-neutral-500 mb-1">
                  E-Posta
                </div>
                <a
                  href="mailto:moduleris@gmail.com"
                  className="text-neutral-200 text-sm hover:text-[#ff5a00] transition-colors"
                >
                  moduleris@gmail.com
                </a>
              </div>
            </li>
          </ul>

          <div className="mt-10 aspect-video border border-neutral-800 overflow-hidden">
            <iframe
              title="MoDuLerİS Konum — Küner, Menderes / İzmir"
              src="https://www.openstreetmap.org/export/embed.html?bbox=27.0900%2C38.1850%2C27.1700%2C38.2350&layer=mapnik&marker=38.2113%2C27.1289"
              className="w-full h-full grayscale-[0.4] contrast-110 brightness-90"
              loading="lazy"
            />
          </div>
        </div>

        {/* Right: form */}
        <div className="lg:col-span-7">
          <form
            onSubmit={submit}
            className="border border-neutral-900 bg-[#0d0d0e] p-8 md:p-12"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="field-label">Ad Soyad / Firma</label>
                <input
                  data-testid={SITE.formName}
                  className="field"
                  type="text"
                  required
                  minLength={2}
                  value={form.name}
                  onChange={onChange("name")}
                  placeholder="Adınız veya firma adı"
                />
              </div>
              <div>
                <label className="field-label">E-Posta</label>
                <input
                  data-testid={SITE.formEmail}
                  className="field"
                  type="email"
                  required
                  value={form.email}
                  onChange={onChange("email")}
                  placeholder="ornek@firma.com"
                />
              </div>
              <div>
                <label className="field-label">Telefon</label>
                <input
                  data-testid={SITE.formPhone}
                  className="field"
                  type="tel"
                  required
                  value={form.phone}
                  onChange={onChange("phone")}
                  placeholder="+90 5xx xxx xx xx"
                />
              </div>
              <div>
                <label className="field-label">İlgilenilen Hizmet</label>
                <select
                  data-testid={SITE.formService}
                  className="field appearance-none"
                  value={form.service}
                  onChange={onChange("service")}
                  required
                >
                  {services.map((s) => (
                    <option key={s} value={s} className="bg-[#0d0d0e]">
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="field-label">Proje Detayları</label>
                <textarea
                  data-testid={SITE.formMessage}
                  className="field min-h-[140px] resize-y"
                  required
                  minLength={5}
                  value={form.message}
                  onChange={onChange("message")}
                  placeholder="Projeniz hakkında kısaca bilgi verin..."
                />
              </div>
            </div>

            <div className="mt-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <p className="text-[11px] font-mono-tech uppercase tracking-[0.24em] text-neutral-500">
                Bilgileriniz gizli tutulur. KVKK kapsamında saklanır.
              </p>
              <button
                data-testid={SITE.formSubmit}
                type="submit"
                disabled={submitting}
                className="btn-accent inline-flex items-center justify-center gap-2 px-8 py-4 text-[12px] uppercase tracking-[0.24em] font-semibold disabled:opacity-60"
              >
                {submitting ? "Gönderiliyor..." : "Teklif İste"}
                <Send size={14} />
              </button>
            </div>

            {ok && (
              <div
                data-testid={SITE.formSuccess}
                className="mt-6 border border-[#ff5a00]/40 bg-[#ff5a00]/10 text-[#ff5a00] px-4 py-3 text-sm"
              >
                Teklif isteğiniz başarıyla alındı. En kısa sürede sizinle iletişime geçeceğiz.
              </div>
            )}
            {err && (
              <div
                data-testid={SITE.formError}
                className="mt-6 border border-red-500/40 bg-red-500/10 text-red-400 px-4 py-3 text-sm"
              >
                {err}
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
